import { css } from "@emotion/css";
import { Button, CounterBadge } from "@octopusdeploy/design-system-components";
import { CheckCircleIcon, ExclamationCircleIcon } from "@octopusdeploy/design-system-icons";
import { borderRadius, borderWidth, space, themeTokens } from "@octopusdeploy/design-system-tokens";
import type { ArgoCDApplicationResource, ProjectResource, EnvironmentV2Resource, ArgoCDInstanceSummaryResource } from "@octopusdeploy/octopus-server-client";
import { useMemo, useState } from "react";
import { isSourceMissingRequireAnnotations, getEnvironmentScopeIssues } from "~/areas/infrastructure/components/ArgoCDInstances/configurationValidation";
import type { DeploymentConfiguration } from "~/components/Actions/argocd/ArgoCDActionCommon";
import { ArgoCDDeploymentPreviewDrawer } from "~/components/Actions/argocd/ArgoCDDeploymentPreviewDrawer";
const styles = {
    container: css({
        display: "flex",
        flexDirection: "column",
        gap: space[12],
        maxWidth: "600px",
    }),
    card: css({
        display: "flex",
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
        padding: space[16],
        borderRadius: borderRadius["large"],
        backgroundColor: themeTokens.color.background.primary.default,
        boxShadow: themeTokens.shadow.extraSmall,
        border: `${borderWidth[1]} solid ${themeTokens.color.border.primary}`,
        span: {
            display: "inline-flex",
            flexDirection: "row",
            alignItems: "center",
            gap: space[8],
        },
    }),
    buttonContainer: css({
        display: "flex",
        justifyContent: "flex-end",
        paddingTop: space[16],
        paddingBottom: space[16],
    }),
};
type ArgoCDApplicationsPreviewProps = {
    applications: ArgoCDApplicationResource[];
    project: ProjectResource;
    deploymentConfiguration?: DeploymentConfiguration;
    refetchArgoContext: () => void;
    environments: EnvironmentV2Resource[];
    gateways?: ArgoCDInstanceSummaryResource[];
};
export const ArgoCDApplicationsPreview = (props: ArgoCDApplicationsPreviewProps) => {
    const { applications, project, deploymentConfiguration, refetchArgoContext, environments, gateways } = props;
    const [drawerIsOpen, setDrawerIsOpen] = useState(false);
    const handleViewPreview = () => {
        setDrawerIsOpen(true);
    };
    const closePreviewDrawer = () => {
        refetchArgoContext();
        setDrawerIsOpen(false);
    };
    const [countSourcesRequiringGitCredentials, countSourcesMissingAnnotations, countSourcesWithInvalidEnvironmentAnnotations, countApplicationsReadyToUpdate] = useMemo(() => {
        let missingGitCredentials = 0;
        let missingAnnotations = 0;
        let invalidEnvironmentAnnotations = 0;
        let readyApplications = 0;
        for (const app of applications) {
            for (const source of app.Sources) {
                if (source.RequiresGitCredential && !source.BestMatchedGitCredential) {
                    missingGitCredentials++;
                }
                if (isSourceMissingRequireAnnotations(source, app)) {
                    missingAnnotations++;
                }
            }
            let hasEnvironmentScopeIssue = false;
            const gatewayEnvironmentIdsForApp = gateways?.find((gateway) => gateway.GatewayId === app.ArgoCDGatewayId)?.EnvironmentIds;
            if (gatewayEnvironmentIdsForApp && gatewayEnvironmentIdsForApp.length > 0) {
                const environmentIssues = getEnvironmentScopeIssues(app, gatewayEnvironmentIdsForApp);
                if (environmentIssues.length > 0) {
                    invalidEnvironmentAnnotations += environmentIssues.length;
                    hasEnvironmentScopeIssue = true;
                }
            }
            const hasGitCredentialIssue = app.Sources.some((source) => source.RequiresGitCredential && !source.BestMatchedGitCredential);
            const hasAnnotationIssue = app.Sources.some((source) => isSourceMissingRequireAnnotations(source, app));
            const isAppReady = !hasGitCredentialIssue && !hasAnnotationIssue && !hasEnvironmentScopeIssue;
            if (isAppReady) {
                readyApplications++;
            }
        }
        return [missingGitCredentials, missingAnnotations, invalidEnvironmentAnnotations, readyApplications];
    }, [applications, gateways]);
    return (<>
            <div className={styles.container}>
                {countApplicationsReadyToUpdate > 0 && (<div className={styles.card}>
                        <span>
                            <CheckCircleIcon size={24} color={themeTokens.color.icon.success}/>
                            <span>Applications ready to update</span>
                        </span>
                        <CounterBadge type={"solid"} label={countApplicationsReadyToUpdate.toString()}/>
                    </div>)}
                {countSourcesRequiringGitCredentials > 0 && (<div className={styles.card}>
                        <span>
                            <ExclamationCircleIcon size={24} color={themeTokens.color.icon.warning}/>
                            <span>Sources missing Git credentials</span>
                        </span>
                        <CounterBadge type={"solid"} label={countSourcesRequiringGitCredentials.toString()}/>
                    </div>)}
                {countSourcesMissingAnnotations > 0 && (<div className={styles.card}>
                        <span>
                            <ExclamationCircleIcon size={24} color={themeTokens.color.icon.warning}/>
                            <span>Sources missing annotations</span>
                        </span>
                        <CounterBadge type={"solid"} label={countSourcesMissingAnnotations.toString()}/>
                    </div>)}
                {countSourcesWithInvalidEnvironmentAnnotations > 0 && (<div className={styles.card}>
                        <span>
                            <ExclamationCircleIcon size={24} color={themeTokens.color.icon.warning}/>
                            <span>Sources with invalid environment annotations</span>
                        </span>
                        <CounterBadge type={"solid"} label={countSourcesWithInvalidEnvironmentAnnotations.toString()}/>
                    </div>)}

                <div className={styles.buttonContainer}>
                    <Button importance="secondary" label="View Applications" onClick={() => handleViewPreview()}/>
                </div>
            </div>
            <ArgoCDDeploymentPreviewDrawer isOpen={drawerIsOpen} onClose={() => closePreviewDrawer()} project={project} deploymentConfiguration={deploymentConfiguration}/>
        </>);
};
